/**
 * Occlusion analysis for stacked video tracks: when an upper track is fully
 * covering the frame with an opaque clip, everything below it needn't be
 * drawn (or decoded). MUST be conservative — a false "opaque" verdict renders
 * the wrong frame, a false "not opaque" only costs performance.
 */
import { activeClipsAt, clipDuration, fadeFactor, transitionWindowAt } from './helpers';
import { sampleTransform } from './params';
import type { Edit, VideoClip } from './types';

/**
 * Does this clip, alone on its track at time t, cover the whole frame
 * opaquely? `coversFrame` answers whether the clip's SOURCE fills the frame
 * (aspect match — the caller knows the drawable's dimensions); default false
 * (no occlusion) keeps this safe when dimensions are unknown.
 */
export function clipIsFullFrameOpaque(
  clip: VideoClip,
  t: number,
  coversFrame: (clip: VideoClip) => boolean,
): boolean {
  if (fadeFactor(clip, clipDuration(clip), t) < 1) return false;
  const sampleT = clip.in + (t - clip.start) * clip.speed;
  const tr = sampleTransform(clip.transform, sampleT);
  if (tr.opacity < 1 || tr.scale < 1 || tr.x !== 0 || tr.y !== 0 || tr.rotate !== 0) return false;
  return coversFrame(clip);
}

/**
 * Index (into edit.tracks) of the topmost video track whose content at t is
 * full-frame opaque — video tracks below it are invisible and can be skipped.
 * Returns -1 when nothing occludes.
 */
export function topmostOpaqueIndex(
  edit: Edit,
  t: number,
  coversFrame: (clip: VideoClip) => boolean,
): number {
  for (let i = edit.tracks.length - 1; i >= 0; i--) {
    const track = edit.tracks[i];
    if (track.kind !== 'video') continue;
    // blended / translucent tracks mix with the pixels below — never occluders
    if (track.blend || (track.opacity ?? 1) < 1) continue;
    if (transitionWindowAt(track, t)) continue; // two blended clips — never opaque-safe
    const active = activeClipsAt(track, t);
    if (active.length !== 1) continue;
    if (clipIsFullFrameOpaque(active[0], t, coversFrame)) return i;
  }
  return -1;
}
